/**
 * <park-rule-group> — one titled CARD of the park's information board: a
 * dark-green pill header carrying a circular icon badge that overlaps the
 * card's left edge, above a stack of `<park-rule>` items (spec
 * `board-visual-language` FR-1 / FR-3; anton/forrest#1402).
 *
 * The board groups its guidance by concern ("Ohjeet ja säännöt", "Siisteys",
 * "Ilmoitathan puutteista"); this is that grouping, and the rules themselves
 * are the SLOTTED content so each item stays server-rendered by
 * `components/ds/ParkRule.astro`.
 *
 * A group with an empty title renders the card WITHOUT a header — which is what
 * pre-existing flat rules content becomes (US-5), so legacy copy still lands on
 * a board card instead of a bare list.
 *
 * Single source of truth = the pure `renderParkRuleGroup()` below, shared by the
 * SSR wrapper and the client element (same contract as `park-section-card`).
 *
 * Attributes:
 *   - title="…"   group heading (omitted → headerless card).
 *   - icon="…"    icon name for the header badge; unknown/missing → paw (FR-4).
 *
 * Emits `.board-group/.board-group__header/.board-group__badge/
 * .board-group__title/.board-group__items` (CSS in ./css/park-rule-board.css).
 *
 * @dsSpec board-visual-language#FR-1 — the board's grouped guidance card.
 */

import { ParkElement, defineElement, esc, html } from "./base";
import { boardIconSvg } from "./board-icons";

const TAG = "park-rule-group";

export interface ParkRuleGroupProps {
	title?: string;
	icon?: string;
}

/**
 * Pure render: (props, slotted items HTML) → inner HTML string. `itemsHtml` is
 * the trusted server-rendered `<park-rule>` markup, emitted verbatim.
 */
export function renderParkRuleGroup(
	props: ParkRuleGroupProps,
	itemsHtml = "",
): string {
	const title = (props.title ?? "").trim();
	const header = title
		? html`<header class="board-group__header"><span class="board-group__badge">${boardIconSvg(props.icon)}</span><h2 class="board-group__title">${esc(title)}</h2></header>`
		: "";
	return html`${header}<div class="board-group__items">${itemsHtml}</div>`;
}

class ParkRuleGroup extends ParkElement {
	static get observedAttributes(): string[] {
		return ["title", "icon"];
	}

	protected render(): string {
		return renderParkRuleGroup(
			{ title: this.attr("title"), icon: this.attr("icon") },
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkRuleGroup);

export { ParkRuleGroup };
